import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AgGridTable, LoadingState } from "components";
import { useReport } from "hooks";
import { S6Query } from "types";
import { formatDateSafe, formatCurrency } from "utils/helpers/accounting";

const today = new Date();
const formatDate = (d: Date) => d.toISOString().slice(0, 10);
const startOfYear = `${today.getFullYear()}-01-01`;
const todayString = formatDate(today);

const defaultFilters: S6Query = {
  from_date: startOfYear,
  to_date: todayString,
};

const S6Report: React.FC = () => {
  const { fetchS6Hkd } = useReport();
  const [filters, setFilters] = useState<S6Query>(defaultFilters);
  const [submitted, setSubmitted] = useState<S6Query | null>(defaultFilters);

  useEffect(() => {
    fetchS6Hkd.mutate(defaultFilters);
  }, [fetchS6Hkd.mutate]);

  const handleFilterChange = useCallback((key: keyof S6Query, value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleSubmit = useCallback(
    (event?: React.FormEvent) => {
      event?.preventDefault();
      if (!filters.from_date || !filters.to_date) {
        return;
      }
      setSubmitted(filters);
      fetchS6Hkd.mutate(filters);
    },
    [fetchS6Hkd, filters],
  );

  const records = (fetchS6Hkd.data?.data ?? []) as any[];

  const summary = useMemo(
    () =>
      records.reduce(
        (acc, item) => {
          if (item.type === "RECEIVABLE") {
            acc.receivable_amount += Number(item.amount ?? 0) || 0;
            acc.receivable_paid += Number(item.paid_amount ?? 0) || 0;
            acc.receivable_balance += Number(item.balance ?? 0) || 0;
          } else if (item.type === "PAYABLE") {
            acc.payable_amount += Number(item.amount ?? 0) || 0;
            acc.payable_paid += Number(item.paid_amount ?? 0) || 0;
            acc.payable_balance += Number(item.balance ?? 0) || 0;
          }
          return acc;
        },
        {
          receivable_amount: 0,
          receivable_paid: 0,
          receivable_balance: 0,
          payable_amount: 0,
          payable_paid: 0,
          payable_balance: 0,
        },
      ),
    [records],
  );

  const netPosition = summary.receivable_balance - summary.payable_balance;

  const columns = useMemo(
    () => [
      {
        headerName: "Ngày hạch toán",
        field: "accounting_date",
        pinned: "left",
        minWidth: 140,
        valueFormatter: (params: any) => formatDateSafe(params.value),
      },
      {
        headerName: "Đối tác",
        field: "partner_name",
        pinned: "left",
        minWidth: 200,
        flex: 1,
      },
      {
        headerName: "Loại",
        field: "type",
        minWidth: 120,
        valueFormatter: (params: any) =>
          params.value === "RECEIVABLE" ? "Phải thu" : "Phải trả",
        cellClass: (params: any) =>
          params.value === "RECEIVABLE"
            ? "text-green-700 font-semibold"
            : "text-red-700 font-semibold",
      },
      {
        headerName: "Số tiền",
        field: "amount",
        minWidth: 140,
        valueFormatter: (params: any) =>
          params.value !== undefined && params.value !== null
            ? formatCurrency(Number(params.value))
            : "",
        cellClass: "text-right font-semibold",
      },
      {
        headerName: "Đã thanh toán",
        field: "paid_amount",
        minWidth: 140,
        valueFormatter: (params: any) =>
          params.value !== undefined && params.value !== null
            ? formatCurrency(Number(params.value))
            : "",
        cellClass: "text-right",
      },
      {
        headerName: "Còn lại",
        field: "balance",
        minWidth: 140,
        valueFormatter: (params: any) =>
          params.value !== undefined && params.value !== null
            ? formatCurrency(Number(params.value))
            : "",
        cellClass: "text-right font-bold text-blue-700",
      },
    ],
    [],
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: "agTextColumnFilter",
      floatingFilter: true,
      resizable: true,
      minWidth: 100,
    }),
    [],
  );

  const isLoading = fetchS6Hkd.isPending;

  return (
    <div className="p-6 space-y-6">
      {isLoading && (
        <LoadingState
          message="Đang tải báo cáo"
          description="Vui lòng chờ trong giây lát."
        />
      )}

      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500 uppercase">Báo cáo S6-HKD</p>
          <h1 className="text-2xl font-semibold text-gray-900">
            Sổ chi tiết thanh toán / công nợ
          </h1>
          <p className="text-sm text-gray-600">
            Theo dõi các khoản phải thu và phải trả.
          </p>
        </div>
        <button
          type="button"
          onClick={handleSubmit}
          className="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-blue-700"
          disabled={isLoading}
        >
          {isLoading ? "Đang tải..." : "Xem báo cáo"}
        </button>
      </div>

      <form
        onSubmit={handleSubmit}
        className="rounded-lg border border-gray-200 bg-white shadow-sm p-4 space-y-4"
      >
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Từ ngày * (ngày hạch toán)
            </label>
            <input
              type="date"
              value={filters.from_date}
              onChange={(e) => handleFilterChange("from_date", e.target.value)}
              className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              required
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Đến ngày * (ngày hạch toán)
            </label>
            <input
              type="date"
              value={filters.to_date}
              onChange={(e) => handleFilterChange("to_date", e.target.value)}
              className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              required
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Loại (tùy chọn)
            </label>
            <select
              value={filters.type || ""}
              onChange={(e) =>
                handleFilterChange("type", e.target.value || undefined)
              }
              className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
            >
              <option value="">Tất cả</option>
              <option value="RECEIVABLE">Phải thu</option>
              <option value="PAYABLE">Phải trả</option>
            </select>
          </div>
        </div>
        <div className="flex items-center justify-end">
          <button
            type="submit"
            className="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            disabled={isLoading}
          >
            Áp dụng bộ lọc
          </button>
        </div>
      </form>

      {submitted && (
        <div className="rounded-lg border border-gray-200 bg-white shadow-sm p-4 space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-blue-900 mb-3">
              📊 Tổng hợp công nợ
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-xs text-gray-600">Phải thu còn lại</p>
                <p className="text-xl font-bold text-green-700">
                  {formatCurrency(summary.receivable_balance)}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Tổng: {formatCurrency(summary.receivable_amount)} | Đã thu:{" "}
                  {formatCurrency(summary.receivable_paid)}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-600">Phải trả còn lại</p>
                <p className="text-xl font-bold text-red-700">
                  {formatCurrency(summary.payable_balance)}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Tổng: {formatCurrency(summary.payable_amount)} | Đã trả:{" "}
                  {formatCurrency(summary.payable_paid)}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-600">Vị thế ròng</p>
                <p
                  className={`text-xl font-bold ${
                    netPosition >= 0 ? "text-green-700" : "text-red-700"
                  }`}
                >
                  {formatCurrency(netPosition)}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {netPosition >= 0 ? "Thuận lợi" : "Bất lợi"}
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold text-gray-900">
              Kết quả ( {submitted.from_date} → {submitted.to_date} )
            </h2>
            <p className="text-sm text-gray-500">{records.length} bản ghi</p>
          </div>

          <AgGridTable
            rows={records}
            columns={columns as any}
            defaultColDef={defaultColDef}
            pagination={false}
            autoHeight
          />
        </div>
      )}
    </div>
  );
};

export default S6Report;
